import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { ForumTopic, ForumComment } from '../types/forum';

const ITEMS_PER_PAGE = 10; // Topics per page
const COMMENTS_PER_PAGE = 20; // Comments per page

export type ForumSortOption = 'recent' | 'comments';
export type SortDirection = 'asc' | 'desc';

export function useForumTopics(
  page = 1,
  sortBy: ForumSortOption = 'recent',
  sortDirection: SortDirection = 'desc'
) {
  const [topics, setTopics] = useState<ForumTopic[]>([]);
  const [totalTopics, setTotalTopics] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        setIsLoading(true);
        
        // Get total count
        const { count } = await supabase
          .from('forum_topics')
          .select('*', { count: 'exact', head: true });

        // Build query based on sort options
        let query = supabase
          .from('forum_topics')
          .select(`
            *,
            animatronic:animatronics(
              id,
              name,
              country,
              park,
              year,
              status,
              media
            ),
            comments:forum_comments(count)
          `);

        // Apply sorting
        if (sortBy === 'recent') {
          query = query.order('last_comment_at', { ascending: sortDirection === 'asc' });
        } else if (sortBy === 'comments') {
          // First get topics with comment counts
          const { data: topicsWithCounts } = await query;
          
          if (topicsWithCounts) {
            // Sort by comment count
            const sorted = topicsWithCounts.sort((a, b) => {
              const countA = a.comments?.[0]?.count || 0;
              const countB = b.comments?.[0]?.count || 0;
              return sortDirection === 'asc' ? countA - countB : countB - countA;
            });
            
            // Apply pagination
            const paginatedData = sorted.slice(
              (page - 1) * ITEMS_PER_PAGE,
              page * ITEMS_PER_PAGE
            );
            
            setTopics(paginatedData);
            setTotalTopics(count || 0);
            setIsLoading(false);
            return;
          }
        }

        // Get paginated topics
        const { data, error } = await query
          .range((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE - 1);

        if (error) throw error;
        setTopics(data || []);
        setTotalTopics(count || 0);
      } catch (err) {
        console.error('Error fetching topics:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch topics'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchTopics();
  }, [page, sortBy, sortDirection]);

  return { topics, totalTopics, totalPages: Math.ceil(totalTopics / ITEMS_PER_PAGE), isLoading, error };
}

export function useForumTopic(id: string | undefined) {
  const [topic, setTopic] = useState<ForumTopic | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchTopic = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('forum_topics')
          .select(`
            *,
            animatronic:animatronics(
              id,
              name,
              country,
              park,
              year,
              status,
              media
            )
          `)
          .eq('id', id)
          .single();

        if (error) throw error;
        setTopic(data);
      } catch (err) {
        console.error('Error fetching topic:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch topic'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchTopic();
  }, [id]);

  return { topic, isLoading, error };
}

export function useForumComments(topicId: string | undefined, page = 1) {
  const [comments, setComments] = useState<ForumComment[]>([]);
  const [totalComments, setTotalComments] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchComments = async () => {
      if (!topicId) return;

      try {
        setIsLoading(true);
        
        // Get total count
        const { count } = await supabase
          .from('forum_comments')
          .select('*', { count: 'exact', head: true })
          .eq('topic_id', topicId);

        // Get paginated comments with user profiles
        const { data, error } = await supabase
          .from('forum_comments')
          .select(`
            *,
            user:user_profiles!user_id(
              display_name,
              avatar_url
            )
          `)
          .eq('topic_id', topicId)
          .order('created_at', { ascending: true })
          .range((page - 1) * COMMENTS_PER_PAGE, page * COMMENTS_PER_PAGE - 1);

        if (error) throw error;
        setComments(data || []);
        setTotalComments(count || 0);
      } catch (err) {
        console.error('Error fetching comments:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch comments'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchComments();
  }, [topicId, page]);

  return { 
    comments, 
    totalComments, 
    totalPages: Math.ceil(totalComments / COMMENTS_PER_PAGE), 
    isLoading, 
    error 
  };
}